import React, { useState } from 'react';
import cn from 'classnames';
import _ from 'lodash';

import {
  useTranslation,
  useAppDispatch,
  useAppSelector,
  useToggleMicrophone,
  useGAEventCommonProps,
} from 'client/hooks';
import {
  selectCurrentRaiseHandMsgTT,
  selectCurrentUserAlreadySpeakTotal,
  selectListenersAllowedToSpeak,
} from 'client/redux/pubnub/selectors';
import { selectUserName, selectUserUID } from 'client/redux/client/selectors';
import { publishMessage, deleteMessage } from 'client/redux/pubnub/actions';
import { selectRoomStatus } from 'client/redux/room/selectors';
import { setListenerHistory } from 'client/redux/room/actions';
import { setIsShowFooterPopUpOverlay } from 'client/redux/ui';
import { selectRoomId } from 'client/redux/room/selectors';
import { MessageType } from 'client/redux/pubnub/types';
import { LS_KEYS } from 'client/redux/pubnub/constants';
import { roomInfoLS, analytics } from 'client/utils';
import { eventBus } from 'client/utils/event-bus';
import { ROOM_STATUS } from 'constants/index';

import Icon from '../Icon';
import RequestPopUp from './RequestPopUp';

import css from './ListenerRequestActions.module.scss';

const getRequestStatus = (isCurrentUserAllowedToSpeak: boolean, currentRaiseHandMsgTT: string | null) => {
  if (isCurrentUserAllowedToSpeak) {
    return 'resolved';
  } else if (currentRaiseHandMsgTT) {
    return 'pending';
  }

  return 'default';
};

interface Props {
  className?: string;
  isAnonymous: boolean;
  showToLoginPopUp: (text: 'emotions' | 'speaking') => void;
}

const ListenerRequestActions: React.FC<Props> = (props) => {
  const { isAnonymous, showToLoginPopUp } = props;

  const { muteMic, unMuteMic } = useToggleMicrophone();
  const { translate } = useTranslation('actionsPanel');

  const roomId = useAppSelector(selectRoomId);
  const userId = useAppSelector(selectUserUID);
  const userName = useAppSelector(selectUserName);
  const roomStatus = useAppSelector(selectRoomStatus);
  const allowedListenersToSpeak = useAppSelector(selectListenersAllowedToSpeak);
  const alreadySpeakTotal = useAppSelector(selectCurrentUserAlreadySpeakTotal);
  const currentRaiseHandMsgTT = useAppSelector(selectCurrentRaiseHandMsgTT);

  const [isBusy, setIsBusy] = useState(false);
  const [isMyMicMuted, setIsMyMicMuted] = useState<boolean>(true);
  const [isShowRequestHandPopUp, setIsShowRequestHandPopUp] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { common } = useGAEventCommonProps();

  const actionsWrapperRef = React.useRef<HTMLDivElement>(null);

  const isRoomFinished = roomStatus === ROOM_STATUS.FINISHED;

  const isCurrentUserAllowedToSpeak = _.findIndex(allowedListenersToSpeak, { id: userId }) >= 0;
  const requestStatus = getRequestStatus(isCurrentUserAllowedToSpeak, currentRaiseHandMsgTT);
  const isMicrophoneIcon = requestStatus === 'resolved';
  const isHandIcon = requestStatus === 'default' || requestStatus === 'pending';
  const isPendingHandIcon = !isMicrophoneIcon && requestStatus === 'pending' && !isRoomFinished;
  const isMutedMicrophone = isMicrophoneIcon && isMyMicMuted;

  React.useEffect(() => {
    setIsBusy(false);
  }, [currentRaiseHandMsgTT]);

  React.useEffect(() => {
    const unsubscribe = eventBus.subscribe<{ isMuted: boolean }>('TOGGLE_LOCAL_MIC', onToggleLocalMic);

    return () => {
      unsubscribe();
    };
  }, []);

  const onToggleLocalMic = (data?: { isMuted: boolean }) => {
    if (!data) return;
    setIsMyMicMuted(data.isMuted);
  };

  const clickOnHandBtn = () => {
    if (isAnonymous) {
      showToLoginPopUp('speaking');
      dispatch(setIsShowFooterPopUpOverlay(true));
      return;
    }

    if (requestStatus === 'default') {
      console.info('SEND_REQUEST');
      setIsBusy(true);
      // Set request permission for listener history
      dispatch(setListenerHistory({ uid: userId, name: userName, roomId: roomId, requestSpeakPermission: 1 }));
      dispatch(
        publishMessage({
          channel: roomId,
          message: JSON.stringify({
            type: MessageType.RAISE_HAND,
            id: userId,
            name: userName,
            alreadySpeakTotal: `${alreadySpeakTotal}`,
          }),
          onPublish: (timetoken) => {
            roomInfoLS.setData(roomId, { [LS_KEYS.LS_RAISE_HAND_MSG_TT]: `${timetoken}` });
          },
        }),
      );

      analytics.gtag.event('audio_chat_user_interaction', {
        ...common,
        ask_premission: 1,
      });
    }

    if (requestStatus === 'pending' && currentRaiseHandMsgTT) {
      console.info('CANCEL_REQUEST');
      setIsBusy(true);
      const lsRoomInfo = roomInfoLS.getData(roomId);
      const isListenerConnectedToCoversation = lsRoomInfo?.isListenerConnectedToCoversation;

      dispatch(
        deleteMessage({
          channel: roomId,
          timeToken: currentRaiseHandMsgTT,
          onDelete: () => {
            if (isListenerConnectedToCoversation) {
              roomInfoLS.setData(roomId, {
                isListenerConnectedToCoversation: null,
                [LS_KEYS.LS_RAISE_HAND_MSG_TT]: null,
              });
            } else {
              roomInfoLS.setData(roomId, { [LS_KEYS.LS_RAISE_HAND_MSG_TT]: null });
            }
          },
        }),
      );
    }

    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: renderText(),
    });
  };

  const clickOnMicrophoneBtn = () => {
    if (requestStatus === 'resolved' && !isMyMicMuted) {
      console.info('MUTE_MICROPHONE');
      muteMic();
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: renderText(),
      });
    }

    if (requestStatus === 'resolved' && isMyMicMuted) {
      console.info('UNMUTE_MICROPHONE');
      unMuteMic();
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: renderText(),
      });
    }
  };

  const closeRequestPopup = () => {
    setIsShowRequestHandPopUp(false);
    dispatch(setIsShowFooterPopUpOverlay(false));
  };

  React.useEffect(() => {
    const lsRoomInfo = roomInfoLS.getData(roomId);
    const isListenerConnectedToCoversation = lsRoomInfo?.isListenerConnectedToCoversation;

    if (requestStatus === 'pending' && !isRoomFinished) {
      dispatch(setIsShowFooterPopUpOverlay(true));
      setIsShowRequestHandPopUp(true);
    }
    if (requestStatus === 'resolved') {
      setIsShowRequestHandPopUp(false);

      if (!isListenerConnectedToCoversation) {
        roomInfoLS.setData(roomId, { isListenerConnectedToCoversation: true });
      }
    }

    if (requestStatus === 'default') {
      muteMic();
      dispatch(setIsShowFooterPopUpOverlay(false));
      setIsShowRequestHandPopUp(false);
    }
  }, [requestStatus]);

  const renderText = () => {
    if (requestStatus === 'default' || isRoomFinished) {
      return translate('btns.handBtnState');
    }

    if (requestStatus === 'pending') {
      return translate('btns.pendingHandBtnState');
    }

    if (requestStatus === 'resolved' && !isMyMicMuted) {
      return translate('btns.microphoneBtnState');
    }

    if (requestStatus === 'resolved' && isMyMicMuted) {
      return translate('btns.mutedMicrophoneBtnState');
    }
  };

  return (
    <div className={css.wrapper} ref={actionsWrapperRef}>
      <div className={cn(css.btn, isBusy && css.disabled)} onClick={isHandIcon ? clickOnHandBtn : clickOnMicrophoneBtn}>
        {isHandIcon ? (
          <div className={cn(css.iconWrapper)}>
            <Icon type={isPendingHandIcon ? 'forbidden_red_hand' : 'hand'} className={cn(css.icon)} />
          </div>
        ) : (
          <div className={cn(css.iconWrapper)}>
            <Icon type={isMutedMicrophone ? 'black_microphone' : 'red_microphone'} className={cn(css.icon)} />
          </div>
        )}
        <div className={css.text}>{renderText()}</div>
      </div>
      <RequestPopUp className={isShowRequestHandPopUp ? 'active' : ''} closePopUp={closeRequestPopup} />
    </div>
  );
};

export default ListenerRequestActions;
